import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

export const EventFilters = ({ onFilterChange, onReset }) => {
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    country: '',
    status: '',
    audience: '',
    minPrice: '',
    maxPrice: '',
    tags: '',
  });

  const [showFilters, setShowFilters] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    onFilterChange(cleanedFilters);
  };

  const handleReset = () => {
    setFilters({
      search: '',
      city: '',
      country: '',
      status: '',
      audience: '',
      minPrice: '',
      maxPrice: '',
      tags: '',
    });
    onReset();
  };

  return (
    <div className="mb-6 rounded-xl bg-white p-4 shadow-sm border border-gray-200">
      {/* Search Bar */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleChange}
            placeholder="Search events by title or description..."
            className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <Filter className="h-5 w-5" />
          Filters
        </button>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="border-t border-gray-200 pt-4 space-y-4 animate-fade-in-up">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Location Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={filters.country}
                onChange={handleChange}
                placeholder="e.g., India"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                value={filters.city}
                onChange={handleChange}
                placeholder="e.g., Mumbai"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Audience Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Audience
              </label>
              <select
                name="audience"
                value={filters.audience}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              >
                <option value="">All Audiences</option>
                <option value="all">All</option>
                <option value="adults">Adults</option>
                <option value="children">Children</option>
                <option value="teens">Teens</option>
                <option value="seniors">Seniors</option>
                <option value="professionals">Professionals</option>
              </select>
            </div>

            {/* Price Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Price
              </label>
              <input
                type="number"
                name="minPrice"
                value={filters.minPrice}
                onChange={handleChange}
                placeholder="0"
                min="0"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Price
              </label>
              <input
                type="number"
                name="maxPrice"
                value={filters.maxPrice}
                onChange={handleChange}
                placeholder="10000"
                min="0"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              />
            </div>

            {/* Tags Filter */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                name="tags"
                value={filters.tags}
                onChange={handleChange}
                placeholder="e.g., music, concert, outdoor"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleReset}
              className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Reset
            </button>
            <button
              onClick={handleApply}
              className="rounded-lg bg-gradient-to-r from-[#F4A696] to-[#E89580] px-6 py-2 font-medium text-white hover:shadow-lg transition-all"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
